<script setup lang="ts">
import { useTemplateRef, watch } from "vue";

import type { QuickSearchItem } from "./api/quickSearch.gql";
import SearchChip from "./components/SearchChip.vue";
import SearchPanel from "./components/SearchPanel.vue";
import SearchTrigger from "./components/SearchTrigger.vue";
import { useQuickSearch } from "./composables/useQuickSearch";

const {
  query,
  isOpen,
  isLoading,
  canSearch,
  results,
  total,
  activeIndex,
  activeSearchQuery,
  close,
  clear,
  clearSearch,
  searchAll,
  handleKeydown,
  selectItem,
  requestFocus,
} = useQuickSearch();

const containerRef = useTemplateRef<HTMLElement>("container");
const triggerRef = useTemplateRef<{ focus: () => void }>("trigger");

watch(requestFocus, () => {
  triggerRef.value?.focus();
});

function onSelect(item: QuickSearchItem) {
  selectItem(item);
}

function onUpdateActiveIndex(index: number | null) {
  activeIndex.value = index;
}
</script>

<template>
  <div
    v-if="canSearch"
    ref="container"
    class="search-root"
  >
    <div class="search-root__trigger">
      <SearchTrigger
        ref="trigger"
        v-model="query"
        :is-open="isOpen"
        @clear="clear"
        @keydown="handleKeydown"
      />
    </div>

    <Transition name="chip">
      <SearchChip
        v-if="activeSearchQuery"
        :query="activeSearchQuery"
        @clear="clearSearch"
      />
    </Transition>

    <SearchPanel
      v-if="isOpen"
      :active-index="activeIndex"
      :loading="isLoading"
      :query="query"
      :results="results"
      :total="total"
      :trigger-el="containerRef"
      @close="close"
      @search-all="searchAll"
      @select="onSelect"
      @update:active-index="onUpdateActiveIndex"
    />
  </div>
</template>

<style scoped>
.search-root {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.search-root__trigger {
  flex: 1;
  min-width: 0;
}

.chip-enter-active {
  transition: opacity 150ms ease-out, transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
}

.chip-leave-active {
  transition: opacity 120ms ease-in, transform 120ms ease-in;
}

.chip-enter-from,
.chip-leave-to {
  opacity: 0;
  transform: scale(0.85) translateX(-4px);
}
</style>
